import axios from "axios";
import { appCheckHeaders } from "./appCheck";
import {
  trackingRequestHeaders,
  acceptTrackingResponse,
  attributionClickSnapshot,
  prepareAttributionRequest,
  finishAttributionRequest,
  marketingSignalHeaders,
  reserveMarketingSignals,
  releaseMarketingSignals,
  acknowledgeMarketingSignals,
} from "../utils/apiAttribution";
import { getDeviceData } from "../utils/deviceInfo";
import { getDeviceId } from "../utils/deviceId";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const IS_DEVELOPMENT = process.env.REACT_APP_ENV === "development";

const getCityId = () => {
  try {
    const cityData = localStorage.getItem("selected_city");

    if (!cityData) {
      return null;
    }

    const city = JSON.parse(cityData);

    return city?.id || null;
  } catch {
    return null;
  }
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const REFRESH_URL = "/v1/auth/user/token/refresh";
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

refreshClient.interceptors.request.use(async (config) => {
  Object.assign(config.headers, await appCheckHeaders());
  Object.assign(config.headers, trackingRequestHeaders(true));
  config._attributionClick = attributionClickSnapshot();
  return config;
});
refreshClient.interceptors.response.use((response) => {
  acceptTrackingResponse(response);
  return response;
});


let refreshInFlight = null;

const expireSession = (expectedRefreshToken = null) => {
  if (expectedRefreshToken && localStorage.getItem("refresh_token") !== expectedRefreshToken) {
    return;
  }
  if (!localStorage.getItem("auth_token") && !localStorage.getItem("refresh_token")) {
    return;
  }
  ["auth_token", "refresh_token", "user_data", "otp_token"].forEach((key) => {
    localStorage.removeItem(key);
  });
  window.dispatchEvent(new CustomEvent("auth_logout"));
};

const requestTokenRefresh = (providedRefreshToken = null) => {
  if (refreshInFlight) return refreshInFlight;

  const currentRefreshToken = localStorage.getItem("refresh_token");
  const previousAccessToken = localStorage.getItem("auth_token");
  const refreshToken = providedRefreshToken || currentRefreshToken;
  if (!refreshToken || refreshToken !== currentRefreshToken) {
    return Promise.reject(new Error("No current refresh token available"));
  }

  const task = (async () => {
    try {
      const deviceId = getDeviceId();
      const response = await refreshClient.post(
        REFRESH_URL,
        { refresh_token: refreshToken },
        deviceId ? { headers: { "X-Device-ID": deviceId } } : undefined,
      );
      const data = response.data?.data;
      if (response.data?.success !== true || !data?.access_token) {
        throw new Error("Token refresh did not return a valid access token");
      }

      if (localStorage.getItem("refresh_token") === refreshToken) {
        localStorage.setItem("auth_token", data.access_token);
        if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
        if (data.user) localStorage.setItem("user_data", JSON.stringify(data.user));
        window.dispatchEvent(new CustomEvent("auth_token_refreshed", {
          detail: { user: data.user || null, previousAccessToken, accessToken: data.access_token },
        }));
      }
      return response.data;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        expireSession(refreshToken);
      }
      throw error;
    }
  })();

  refreshInFlight = task;
  task.then(
    () => { if (refreshInFlight === task) refreshInFlight = null; },
    () => { if (refreshInFlight === task) refreshInFlight = null; },
  );
  return task;
};

apiClient.interceptors.request.use(
  async (config) => {
    Object.assign(config.headers, await appCheckHeaders());
    const token = localStorage.getItem("auth_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    else delete config.headers.Authorization;

    const cityId = getCityId();
    if (cityId) config.headers["X-City-ID"] = cityId;
    const deviceId = getDeviceId();
    if (deviceId) config.headers["X-Device-ID"] = deviceId;
    if (!config._attributionOwner || !config._retry) {
      const attribution = await prepareAttributionRequest();
      config._attributionOwner = attribution.owner;
      config._attributionClick = attribution.click;
      Object.assign(config.headers, attribution.headers);
    } else {
      Object.assign(config.headers, trackingRequestHeaders());
    }

    if (!config._retry && !config._skipMarketingSignals) {
      config._marketingReservation = reserveMarketingSignals();
    }
    delete config.headers["X-Marketing-Signals"];
    if (!config._skipMarketingSignals) {
      Object.assign(config.headers, marketingSignalHeaders(config._marketingReservation));
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => {
    acceptTrackingResponse(response);
    acknowledgeMarketingSignals(response);
    releaseMarketingSignals(response.config?._marketingReservation);
    finishAttributionRequest(response.config?._attributionOwner);
    return response;
  },
  async (error) => {
    const request = error.config;
    const publicAuthPaths = [
      "/v1/auth/user/otp/send",
      "/v1/auth/user/otp/verify",
      "/v1/auth/user/register",
      REFRESH_URL,
    ];
    
    if (error.response?.status !== 401 || !request || request._retry ||
        publicAuthPaths.some((path) => request.url?.includes(path))) {
      finishAttributionRequest(request?._attributionOwner);
      releaseMarketingSignals(request?._marketingReservation);
      return Promise.reject(error);
    }

    const sentToken = request.headers?.Authorization;
    if (!sentToken) {
      finishAttributionRequest(request?._attributionOwner);
      releaseMarketingSignals(request?._marketingReservation);
      return Promise.reject(error);
    }
    request._retry = true;
    const storedToken = localStorage.getItem("auth_token");
    const refreshToken = localStorage.getItem("refresh_token");

    
    if (storedToken && sentToken !== `Bearer ${storedToken}`) {
      request.headers.Authorization = `Bearer ${storedToken}`;
      return apiClient(request);
    }

    if (!refreshToken) {
      finishAttributionRequest(request?._attributionOwner);
      releaseMarketingSignals(request?._marketingReservation);
      expireSession();
      return Promise.reject(error);
    }

    try {
      await requestTokenRefresh(refreshToken);
      const accessToken = localStorage.getItem("auth_token");
      if (!accessToken) {
        finishAttributionRequest(request?._attributionOwner);
        releaseMarketingSignals(request?._marketingReservation);
        return Promise.reject(error);
      }
      request.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(request);
    } catch (refreshError) {
      finishAttributionRequest(request?._attributionOwner);
      releaseMarketingSignals(request?._marketingReservation);
      if (IS_DEVELOPMENT) console.error("Token refresh failed:", refreshError);
      return Promise.reject(refreshError);
    }
  },
);

export const api = {

  async recordPageView(url, navigationId) {
    return apiClient.post("/v1/marketing/page-view", {
      url,
      navigation_id: navigationId,
    }, { _skipMarketingSignals: true });
  },
  async recordSellFormOpened(signal) {
    if (!signal) return;
    await apiClient.post("/v1/marketing/sell-form-open", null, {
      _skipMarketingSignals: true,
    });
  },
  async searchCities(query) {
    try {
      const response = await apiClient.get("/v1/location/cities/search", {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Search Cities Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },
  async findCityBySlug(slug) {
    try {
      const response = await apiClient.get(`/v1/location/cities/find/${slug}`);
      console.log("API response:", response.data);
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Find City By Slug Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async getTopCities() {
    try {
      const response = await apiClient.get("/v1/location/cities/top");
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Top Cities Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async getPincodeAddress(pincode, categoryId, brandId) {
    try {
      const response = await apiClient.get(
        `/v1/location/pincode/${pincode}/addresses`,
        { params: categoryId && brandId ? { category_id: categoryId, brand_id: brandId } : {} },
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Pincode Address Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },


  async getHomeData() {
    try {
      const response = await apiClient.get("/v1/home");
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Home Data Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },


  async getCategoryBrands(categorySlug) {
    try {
      const response = await apiClient.get(
        `/v1/catalog/categories/${categorySlug}/brands`,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Category Brands Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async getBrandProducts(categorySlug, brandSlug) {
    try {
      const response = await apiClient.get(
        `/v1/catalog/brands/${categorySlug}/${brandSlug}/products`,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Brand Products Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async getProductBySlug(categorySlug, brandSlug, productSlug) {
    try {
      const response = await apiClient.get(
        `/v1/catalog/categories/${categorySlug}/brands/${brandSlug}/products/${productSlug}`,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Product By Slug Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },
  async getVariantBySlug(categorySlug, brandSlug, productSlug, variantSlug) {
    try {
      const response = await apiClient.get(
        `/v1/catalog/categories/${categorySlug}/brands/${brandSlug}/products/${productSlug}/variants/${variantSlug}`,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Product By Slug Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async searchProducts(query, { limit = 5, offset = 0, cityId, signal } = {}) {
    try {
      const response = await apiClient.get('/v1/catalog/products/search', {
        params: {
          searchQuery: query.trim(),
          limit,
          offset,
          city_id: cityId ?? getCityId(),
        },
        signal,
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT && error.code !== 'ERR_CANCELED') {
        console.error('Search Products Error:', error.response?.data || error.message);
      }
      throw error;
    }
  },

  async getFaults(brandSlug, productSlug, variationSlug) {
    try {
      const response = await apiClient.get(
        `/v1/devices/${brandSlug}/${productSlug}/${variationSlug}/faults`,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Faults Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async sendOTP(mobileNumber) {
    try {
      const response = await apiClient.post("/v1/auth/user/otp/send", {
        mobile: mobileNumber,
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error("Send OTP Error:", error.response?.data || error.message);
      }
      throw error;
    }
  },

  async resendOTP(mobileNumber, otpToken) {
    try {
      const response = await apiClient.post("/v1/auth/user/otp/send", {
        mobile: mobileNumber,
        otp_token: otpToken,
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Resend OTP Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async verifyOTP(mobileNumber, otp, otpToken, fcmToken = null) {
    try {
      const deviceData = getDeviceData(fcmToken);
        deviceData.device_id = getDeviceId();
      const response = await apiClient.post("/v1/auth/user/otp/verify", {
        mobile: mobileNumber,
        otp: otp,
        otp_token: otpToken,
        device_data: deviceData,
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Verify OTP Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async register(userData) {
    try {
      const deviceData = getDeviceData(userData.fcmToken);
        deviceData.device_id = getDeviceId();
      const response = await apiClient.post("/v1/auth/user/register", {
        name: userData.name,
        email: userData.email,
        mobile: userData.mobile,
        city_id: userData.city_id,
        pincode: userData.pincode ?? "",
        address: userData.address ?? "",
        otp: userData.otp,
        otp_token: userData.otp_token,
        device_data: deviceData,
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error("Register Error:", error.response?.data || error.message);
      }
      throw error;
    }
  },

  async refreshToken(refreshToken) {
    return requestTokenRefresh(refreshToken);
  },

  async getProfile() {
    try {
      const response = await apiClient.get("/v1/auth/user/me");
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Profile Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async updateProfile(profileData) {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/profile",
        profileData,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Update Profile Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async storeFcmToken(fcmToken) {
    try {
      const response = await apiClient.post("/v1/auth/user/fcm-token", {
        fcm_token: fcmToken,
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Store FCM Token Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async updateBankDetails(bankData) {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/bank-details",
        bankData,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Update Bank Details Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async logout() {
    try {
      const response = await apiClient.post("/v1/auth/user/logout");
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error("Logout Error:", error.response?.data || error.message);
      }
      throw error;
    }
  },

  async getUserAddresses() {
    try {
      const response = await apiClient.get("/v1/auth/user/addresses");
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get User Addresses Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async saveAddress(addressData) {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/addresses",
        addressData,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Save Address Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async editAddress(addressId, addressData) {
    try {
      const response = await apiClient.put(
        `/v1/auth/user/addresses/${addressId}`,
        addressData,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Edit Address Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async deleteAddress(addressId) {
    try {
      const response = await apiClient.delete(
        `/v1/auth/user/addresses/${addressId}`,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Delete Address Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async getServiceRequests(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (
          filters[key] !== undefined &&
          filters[key] !== null &&
          filters[key] !== ""
        ) {
          queryParams.append(key, filters[key]);
        }
      });

      const url = `/v1/auth/user/service-requests${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Service Requests Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async downloadServiceRequestReceipt(serviceId) {
    const response = await apiClient.get(
      `/v1/auth/user/service-requests/${encodeURIComponent(serviceId)}/receipt`,
      { responseType: "blob" },
    );
    const filename = response.headers["x-document-filename"];
    return {
      blob: response.data,
      filename: typeof filename === "string" ? filename : null,
    };
  },

  async createServiceRequest(payload) {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/service-requests",
        payload,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Create Service Request Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async savePincode(pincode, variationId, evaluationId) {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/service-requests/pincode",
        {
          pincode: pincode,
          variation_id: variationId,
          evaluation_id: evaluationId,
        },
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Save Pincode Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async validateEvaluation(payload) {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/service-requests/evaluation/validate",
        payload,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Validate Evaluation Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  applyCoupon: async (brandSlug, productSlug, variationSlug, couponCode) => {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/service-requests/apply-coupon",
        {
          brand_slug: brandSlug,
          product_slug: productSlug,
          variation_slug: variationSlug,
          coupon_code: couponCode,
        },
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Apply Coupon Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  removeCoupon: async (brandSlug, productSlug, variationSlug) => {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/service-requests/remove-coupon",
        {
          brand_slug: brandSlug,
          product_slug: productSlug,
          variation_slug: variationSlug,
        },
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Remove Coupon Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async getCancelRescheduleReasons(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.type) {
        queryParams.append("type", params.type);
      }
      const url = `/v1/auth/user/service-requests/reasons${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Get Reasons Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async cancelServiceRequest(serviceId, payload) {
    try {
      const response = await apiClient.post(
        `/v1/auth/user/service-requests/communication/${serviceId}/cancel`,
        payload,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Cancel Service Request Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async rescheduleServiceRequest(serviceId, payload) {
    try {
      const response = await apiClient.post(
        `/v1/auth/user/service-requests/communication/${serviceId}/reschedule`,
        payload,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Reschedule Service Request Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },

  async calculateDevicePrice(payload) {
    try {
      const response = await apiClient.post(
        "/v1/auth/user/service-requests/inspection/price-calculation",
        payload,
      );
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Calculate Price Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },


  async contact(contactData) {
    try {
      const response = await apiClient.post("/v1/contacts", {
        name: contactData.name,
        email: contactData.email,
        mobile: contactData.mobile,
        subject: contactData.subject,
        message: contactData.message,
      });
      return response.data;
    } catch (error) {
      if (IS_DEVELOPMENT) {
        console.error(
          "Contact API Error:",
          error.response?.data || error.message,
        );
      }
      throw error;
    }
  },



};

export default apiClient;
